import { Button, Modal, Tabs } from "@pickit/pickit-components";
import "./CapacityDetailModal.scss";
import { CapacityDetailModalProps } from "./types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import "./i18n";
import {
  DashboardDropdownState,
  SlotPickers,
} from "reducers/types/dashboardSlotCapacityModal";
import { useCallback, useEffect, useMemo } from "react";
import moment from "moment";
import { CAPACITY_DETAIL_PARAM_TABS, DATE_FORMATS } from "utils/constants";
import { formatHHMM } from "utils/date";

const CapacityDetailModal = ({
  isOpen,
  dropdownState,
  slotDetail,
  toggleModal,
  getSlotDetail,
  setDropdownState,
}: CapacityDetailModalProps) => {
  const { t } = useTranslation("capacityDetailModal");
  const tabsOrder: DashboardDropdownState[] = useMemo(
    () => ["withTrx", "withoutTrx", "disconnected"],
    []
  );

  const initialTabIndex = useMemo(
    () => tabsOrder.indexOf(dropdownState),
    [dropdownState, tabsOrder]
  );

  const tabPickers: { [key in DashboardDropdownState]: SlotPickers[] } =
    useMemo(
      () => ({
        withTrx: slotDetail?.pickers.withTransaction,
        withoutTrx: slotDetail?.pickers.withoutTransaction,
        disconnected: slotDetail?.pickers.disconnected,
      }),
      [slotDetail?.pickers]
    );

  const maxAmount = Math.max(
    tabPickers?.withTrx?.length,
    tabPickers?.withoutTrx?.length,
    tabPickers?.disconnected?.length
  );

  const tabHeight = Math.min(292, Math.max(230, 40 + 36 * maxAmount));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set('modal', 'capacity-detail-modal');
      params.set('tab', CAPACITY_DETAIL_PARAM_TABS.withTrx);
    } else {
      params.delete('modal');
      params.delete('tab');
    }
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [isOpen]);

  const resetUrl = useCallback((tab: DashboardDropdownState) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', CAPACITY_DETAIL_PARAM_TABS[tab]);
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, []);
  

  return (
    <Modal
      width="960px"
      height="fit-content"
      isOpen={isOpen}
      onClose={() => toggleModal(false)}
      testId="capacity-detail-modal"
    >
      <div className="capacity-slot-info-container" data-test-id="capacity-detail-modal-info">
        <h3 className="capacity-slot-region" data-test-id="capacity-detail-modal-region">{slotDetail?.region?.name}</h3>
        <h4 className="capacity-slot-workzone" data-test-id="capacity-detail-modal-workzone">{slotDetail?.workZone?.name}</h4>
        <p className="capacity-slot-time" data-test-id="capacity-detail-modal-time">
          {moment(slotDetail?.startAt).format(DATE_FORMATS.shortDate)}
        </p>
        <p className="capacity-slot-time" data-test-id="capacity-detail-modal-time-range">
          {slotDetail?.startAt && formatHHMM(slotDetail?.startAt)} a{" "}
          {slotDetail?.finishAt && formatHHMM(slotDetail?.finishAt)}
        </p>
      </div>
      <div className="tabs-container" data-test-id="capacity-detail-modal-tabs">
        <Tabs
          initialIndex={initialTabIndex}
          tabs={[
            { title: t("pickersWithTrx"), testId: "capacity-detail-modal-tab-with-trx" },
            { title: t("pickersWithoutTrx"), testId: "capacity-detail-modal-tab-without-trx" },
            { title: t("pickersDisconnected"), testId: "capacity-detail-modal-tab-disconnected" },
          ]}
          size="minimal"
          width="180px"
          adjustableWidth
          onChange={(action, index) => {
            const selectedTab = tabsOrder[index as number];
            setDropdownState(selectedTab);
            action();
            resetUrl(selectedTab);
          }}
        >
          <>
            {tabsOrder.map((tab) => (
              <div
                className="pickers-tab-container"
                style={{
                  height: tabHeight,
                }}
                data-testid={`capacity-detail-modal-tab-${tab}`}
              >
                {tabPickers[tab]?.length !== 0 ? (
                  <>
                    <div className="pickers-tab-container-row capacity-pickers-info-titles">
                      <h3 className="pickers-tab-container-col-2" data-test-id="capacity-detail-modal-picker-id-header">
                        {t("pickerId")}
                      </h3>
                      <h3 className="pickers-tab-container-col-3" data-test-id="capacity-detail-modal-picker-name-header">
                        {t("pickerName")}
                      </h3>
                      <h3 className="pickers-tab-container-col-3" data-test-id="capacity-detail-modal-picker-whatsapp-header">
                        {t("whatsapp")}
                      </h3>
                      {tab === "withTrx" && (
                        <>
                          <h3 className="pickers-tab-container-col-2" data-test-id="capacity-detail-modal-picker-trip-id-header">
                            {t("tripId")}
                          </h3>
                          <h3 className="pickers-tab-container-col-2" data-test-id="capacity-detail-modal-picker-trx-amount-header">
                            {t("trxAmount")}
                          </h3>
                        </>
                      )}
                    </div>
                    <hr
                      className={classNames("pickers-separate", {
                        "pickers-separate-small": tab !== "withTrx",
                      })}
                    />
                    {tabPickers[tab]?.map((picker) => (
                      <>
                        <div className="pickers-tab-container-row capacity-pickers-info">
                          <p className="pickers-tab-container-col-2" data-test-id="capacity-detail-modal-picker-id">
                            {picker.pickerId}
                          </p>
                          <p className="pickers-tab-container-col-3 picker-tab-name" data-test-id="capacity-detail-modal-picker-name">
                            {picker.firstName} {picker.surname}
                          </p>
                          <div className="pickers-tab-container-col-3" data-test-id="capacity-detail-modal-picker-whatsapp">
                            <a
                              href={`https://api.whatsapp.com/send/?phone=${
                                picker.phoneNumber
                              }&text=${t("contactText", {
                                name: `${picker.firstName}`,
                              })}&type=phone_number&app_absent=0`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {picker.phoneNumber}
                            </a>
                          </div>
                          {tab === "withTrx" && (
                            <>
                              <p className="pickers-tab-container-col-2" data-test-id="capacity-detail-modal-picker-trip-id">
                                {picker.tripCode}
                              </p>
                              <p className="pickers-tab-container-col-2" data-test-id="capacity-detail-modal-picker-trx-amount">
                                {picker.pendingTransactions}/
                                {picker.totalTransactions}
                              </p>
                            </>
                          )}
                        </div>
                        <hr
                          className={classNames("pickers-separate", {
                            "pickers-separate-small": tab !== "withTrx",
                          })}
                        />
                      </>
                    ))}
                  </>
                ) : (
                  <div className="no-picker-container" data-test-id="capacity-detail-modal-no-pickers">
                    {t("noPickersText")}
                  </div>
                )}
              </div>
            ))}
          </>
        </Tabs>
        <div className="bottom-button-container">
          <div className="refresh-button" data-test-id="capacity-detail-modal-refresh-button">
            <Button
              label={t("global:update")}
              type="button"
              size="medium"
              onClick={() => getSlotDetail(slotDetail.id)}
              testId="capacity-detail-modal-refresh-button"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CapacityDetailModal;
